import { call, fork, put, select, take, takeEvery } from "typed-redux-saga";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { callApi } from "../sagas";
import { METADATA_FILENAME_ATTRIBUTE, ModelType, OutputMetadataItem } from "../global-types/customization-types";
import { getApiErrorMessage } from "../../util/errors";
import { hasBlockingFormValidationErrors } from "../global-types/store-errors";
import { SaveResult, buildSaveFinishResult } from "../../web-apis/save-response";
import { adaptSelectors, allModelCustomizationsReset, customizationBaseDuplicated, customizationDuplicationItemsAdded, customizationOutputReplaced, modelCustomizationExported, modelCustomizationExportFailed, modelCustomizationImported, modelCustomizationImportFailed, modelCustomizationObjectsSetAsModified, modelCustomizationPdfExportRequested, modelCustomizationSaved, modelCustomizationsSet, pdfExportFinished, pdfExportStarted, resetAllModelCustomizationsFinished, resetAllModelCustomizationsStarted, resetSingleOutputFinished, resetSingleOutputStarted, saveModelCustomizationFinished, saveModelCustomizationStarted, singleCustomizationReset } from "./adaptSlice";
import { Action } from "@reduxjs/toolkit";
import { AdaptCustomizationEntities, AdaptCustomizationEntitiesForExport, AdaptCustomizationOutput, AdaptRoiRule, EXPORT_ADAPT_JSON_FILE_PREFIX } from "./adapt-types";
import { cloneDeep } from "lodash-es";
import { createNewModelCustomizationBase, duplicateModelCustomizationMetadataItem, generateNewId } from "../global-types/customization-helpers";
import { DuplicatedIdMap } from "../global-types/reducer-helpers";
import { ensureFilenameHasDcmExtension } from "../../pages/customization/file-operations";
import { convertAdaptViewModelsToJson, createNewAdaptCustomizationOutput, createNewAdaptRoiSelection, duplicateRoiRule } from "./adapt-helpers";
import { modelCustomizationPageFocusSet } from "../appStatus/appStatusSlice";
import { appConfigSelectors } from "../appConfig/appConfigSlice";
import { DeploymentMode } from "../appConfig/appDeploymentInfoTypes";
import { downloadBlobToDisk, downloadFileToDisk } from "../../util/files";

/**
 * Saga to fetch adapt customizations using the provided application client.
 *
 * This saga function performs an API call to retrieve adapt customizations
 * and handles the success or failure of the operation. On success, it updates
 * the store with the fetched customizations. On failure, it updates the store
 * with an error message.
 * 
 * @param appClient - An instance of `MVisionAppClient` or `undefined`. If `undefined`,
 *                    the saga will return `null` for adapt customizations.
 * 
 * @returns The result of the API call, which is either the fetched adapt customizations
 *          or `null` if the operation fails or the `appClient` is `undefined`.
 */
export function* fetchAdaptCustomizationsSaga(appClient: MVisionAppClient | undefined) {

    const result = yield* callApi({
        doApiCall: function* (client) {
            const adaptCustomizations = appClient === undefined ? null : yield* call(async () => client.fetchAdaptCustomizationsAsync(appClient));
            if (adaptCustomizations !== null) {
                const fetchModels = yield* call(async () => client.fetchAvailableModelsAsync(appClient, ModelType.Adapt));
                if (fetchModels !== undefined) {
                    // if the available models collection is in use, mark which segmentation models are unavailable
                    for (const model of adaptCustomizations.models) {
                        model.isAvailable = fetchModels.availableModels.includes(model.modelName);
                    }
                } else {
                    throw new Error('Could not retrieve list of available Adapt+ models');
                }
            }
            return adaptCustomizations;
        },
        onSuccess: function* (result) {
            yield* put(modelCustomizationsSet({ customizations: result }));
        },
        onFailure: function* (error) {
            yield* put(modelCustomizationsSet({ customizations: null, errorMessage: error.message || 'Unspecified error.' }));
        },
        // onFinish: function* (result: any | undefined, error: Error | BackendValidationError | null) {
        //     console.log(result)
        //     console.log(error)
        // }
    });

    return result;
}

function* saveAdaptCustomizationSaga(action: Action) {
    if (modelCustomizationSaved.match(action)) {
        const { configurationTarget, force } = action.payload;


        yield* callApi({
            doApiCall: function* (client) {
                // collect model customization -- this is essentially a cache database dump
                const modelCustomization: AdaptCustomizationEntitiesForExport = yield* collectAdaptCustomizationForExport();

                // sanity-check that everything is valid in case invalid data slips through the UI debouncing
                const formValidationErrors = yield* select(adaptSelectors.selectFormValidationErrors);
                if (hasBlockingFormValidationErrors(formValidationErrors)) {
                    throw new Error(`Adapt+ model configuration has invalid entries. Please fix them before saving.`);
                }

                yield* put(saveModelCustomizationStarted());
                const result = yield* call(async () => client.saveAdaptCustomizationAsync(configurationTarget, modelCustomization, force));
                return result;
            },
            onFinish: function* (result: SaveResult | undefined, error) {
                const r = buildSaveFinishResult(result, error, ModelType.Adapt);
                yield* put(saveModelCustomizationFinished({
                    saveWasSuccessful: r.success,
                    errorMessage: r.errorMessage,
                    error: r.validationError,
                    concurrentSaveError: r.concurrentSaveError,
                    updatedTimestamps: r.updatedTimestamps,
                }));
                return r.success;
            }
        });
    }
}

function* duplicateAdaptCustomizationSaga(action: Action) {
    if (customizationBaseDuplicated.match(action)) {
        const { customizationBaseId, newCustomizationName } = action.payload;

        const sourceCustomizationBase = yield* select(adaptSelectors.selectCustomizationBaseById, customizationBaseId);
        if (!sourceCustomizationBase) { throw new Error(`Could not retrieve customization base with id ${customizationBaseId}`); }
        const parentSegmentationModel = yield* select(adaptSelectors.selectModelById, sourceCustomizationBase.modelId);
        if (!parentSegmentationModel) { throw new Error(`Could not retrieve parent model with id ${sourceCustomizationBase.modelId}`); }

        const newCustomizationBaseId = generateNewId();

        // collect and map all ids we duplicate here so we can also duplicate related form errors to new targets
        const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: customizationBaseId, targetId: newCustomizationBaseId }];

        // duplicate customization outputs (note: triggers are NOT duplicated)
        const newOutputs: AdaptCustomizationOutput[] = [];
        let newRoiRules: AdaptRoiRule[] = [];
        let newMetadata: OutputMetadataItem[] = [];
        for (const outputId of sourceCustomizationBase.outputs) {
            const sourceCustomizationOutput = yield* select(adaptSelectors.selectOutputById, outputId);
            if (!sourceCustomizationOutput) { throw new Error(`Could not retrieve customization output with id ${outputId}`); }
            const duplicationEntities = yield* duplicateCustomizationOutputFromStore(sourceCustomizationOutput, undefined, newCustomizationBaseId);
            newOutputs.push(duplicationEntities.duplicatedOutput);
            newRoiRules = newRoiRules.concat(duplicationEntities.newRoiRules);
            newMetadata = newMetadata.concat(duplicationEntities.newMetadata);

            duplicatedIds.push(...duplicationEntities.duplicatedIds);
        }

        // duplicate customization base
        const duplicatedCustomizationBase = createNewModelCustomizationBase(
            newCustomizationName,
            sourceCustomizationBase.description,
            newOutputs.map(o => o.id),
            [],
            [],
            sourceCustomizationBase.modelId,
            newCustomizationBaseId,
            true
        );

        // store everything
        yield* put(customizationDuplicationItemsAdded({
            parentModelId: parentSegmentationModel.id,
            customization: duplicatedCustomizationBase,
            outputs: newOutputs,
            metadata: newMetadata,
            roiRules: newRoiRules,
            duplicatedIds: duplicatedIds,
        }));

        // focus on the new duplicate
        if (newOutputs.length > 0) {
            yield* put(modelCustomizationPageFocusSet(newOutputs[0].id));
        }
    }
}

function* resetAllAdaptCustomizationsSaga(action: Action) {
    if (allModelCustomizationsReset.match(action)) {
        const configurationTarget = action.payload;

        yield* callApi({
            doApiCall: function* (client) {
                yield* put(resetAllModelCustomizationsStarted());

                let success = yield* call(async () => client.deleteAdaptCustomizationAsync(configurationTarget));
                if (success) {
                    // immediately fetch updated (reset) customizations
                    success = yield* call(fetchAdaptCustomizationsSaga, configurationTarget);
                }

                return success;
            },
            onFinish: function* (result, error) {
                let errorMessage = null;
                const success = result || false;

                if (error !== null) {
                    console.error('Was not able to reset Adapt+ customizations');
                    errorMessage = getApiErrorMessage(error);
                    console.error(errorMessage);
                } else if (!success) {
                    errorMessage = 'Unable to reset Adapt+ customizations. There might be a problem with the server.';
                }


                yield* put(resetAllModelCustomizationsFinished({
                    resetWasSuccessful: success,
                    errorMessage: errorMessage || undefined
                }));

                return success;
            },
        });
    }
}

/** Resets a single Adapt+ CUSTOMIZATION BASE (not output) */
function* resetSingleAdaptCustomizationSaga(action: Action) {
    if (singleCustomizationReset.match(action)) {

        // NOTE: although we're technically resetting an entire customization base here, in practice
        // this code only resets a single customization output. Currently Adapt+ does not support
        // having multiple output files per customization so resetting just the single default output
        // file is fine. This approach must be revised if we ever support adding multiple output
        // files per customization for Adapt+.
        const customizationId = action.payload;

        const customization = yield* select(adaptSelectors.selectCustomizationBaseById, customizationId);

        if (!customization) { throw new Error('Could not find correct customization for reset'); }
        if (customization.outputs.length !== 1) { throw new Error('Expected customization to have exactly one output -- cannot reset'); }

        const output = yield* select(adaptSelectors.selectOutputById, customization.outputs[0]);
        if (!output) { throw new Error('Could not find correct customization output for reset'); }

        const customizationOutputId = output.id;
        const modelName = yield* select(adaptSelectors.selectModelNameForOutput, customizationOutputId);
        if (!modelName) { throw new Error('No model name specified, cannot fetch default configuration for model'); }

        yield* callApi({
            doApiCall: function* (client) {
                yield* put(resetSingleOutputStarted());

                // get default config for this model
                return yield* call(async () => client.fetchDefaultConfigurationForAdaptOutput(modelName));
            },
            onFinish: function* (result, error) {
                if (result) {
                    // replace existing output and its items with received default items
                    yield* put(customizationOutputReplaced({ customizationOutputId, newOutputEntities: result }));
                }

                let errorMessage = null;
                if (error !== null) {
                    console.error(`Was not able to reset single customization output (${customizationOutputId})`);
                    errorMessage = `An error occurred when trying to reset target file: ${getApiErrorMessage(error)}`;
                    console.error(errorMessage);
                }

                const success = error === null;
                yield* put(resetSingleOutputFinished({
                    resetWasSuccessful: success,
                    errorMessage: errorMessage || undefined
                }));

                return success;
            }
        });
    }
}

function* exportAdaptCustomization(appClient: MVisionAppClient | undefined) {
    try {
        // collect model customization -- this is essentially a cache database dump
        const modelCustomization: AdaptCustomizationEntitiesForExport = yield* collectAdaptCustomizationForExport();
        const jsonObject = yield* call(async () => convertAdaptViewModelsToJson(modelCustomization, true));

        // Default filename if appClient?.userName is undefined or empty
        const defaultFilename = "model-customizations.json";
        const localModeFilename = "local.json";
        const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        const isLocalMode = appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local);
        const filename = `${EXPORT_ADAPT_JSON_FILE_PREFIX}${isLocalMode ? localModeFilename : (appClient?.userName ? `${appClient.userName}.json` : defaultFilename)}`;

        downloadFileToDisk(JSON.stringify(jsonObject, null, 2), filename, 'application/json');
        yield* put(modelCustomizationExportFailed(null)); // resets any failure error message
    } catch (error) {
        console.log(`An error occurred in exportAdaptCustomization:`);
        console.error(error);

        const errorMessage = getApiErrorMessage(error, 'Adapt+ JSON export failed.');
        yield* put(modelCustomizationExportFailed(errorMessage));
    }
}

function* importAdaptCustomization(modelCustomization: AdaptCustomizationEntities) {
    try {
        yield* put(modelCustomizationsSet({ customizations: modelCustomization, retainOriginalData: true }));
        yield* put(modelCustomizationObjectsSetAsModified(modelCustomization)); // set the entire ModelCustomizationEntities object as modified
        yield* put(modelCustomizationImportFailed(null));  // resets any failure error message
    } catch (error) {
        const errorMessage = getApiErrorMessage(error, 'Adapt+ JSON import failed.');
        yield* put(modelCustomizationImportFailed(errorMessage));
    }
}


/// helper functions for adapt config sagas

export function* collectAdaptCustomizationForExport() {
    const modelCustomization: AdaptCustomizationEntitiesForExport = {
        models: yield* select(adaptSelectors.selectModels),
        customizationBases: yield* select(adaptSelectors.selectCustomizationBases),
        adaptOutputs: yield* select(adaptSelectors.selectOutputs),
        outputMetadata: yield* select(adaptSelectors.selectOutputMetadata),

        adaptRoiRules: yield* select(adaptSelectors.selectRoiRules),

        aeTitleRules: yield* select(adaptSelectors.selectAeTitleRules),
        dicomRules: yield* select(adaptSelectors.selectDicomRules),
        dicomAttributeRules: yield* select(adaptSelectors.selectDicomAttributeRules),
    };

    return modelCustomization;
}

type DuplicatedOutputEntities = {
    duplicatedOutput: AdaptCustomizationOutput;
    newRoiRules: AdaptRoiRule[];
    newMetadata: OutputMetadataItem[];
    duplicatedIds: DuplicatedIdMap[];
};

/**
 * Duplicates a customization output from store.
 * 
 * This should only be called from within related sagas (or from unit tests).
 * 
 */
function* duplicateCustomizationOutputFromStore(
    sourceCustomizationOutput: AdaptCustomizationOutput,
    newFilename?: string,
    newCustomizationBaseId?: string) {

    const newRoiRules: AdaptRoiRule[] = [];
    const newMetadata: OutputMetadataItem[] = [];
    const newCustomizationOutputId = generateNewId();
    const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: sourceCustomizationOutput.id, targetId: newCustomizationOutputId }];

    let duplicateFilename: string = '';

    // duplicate metadata
    for (const metadataId of sourceCustomizationOutput.metadata) {
        const metadata = yield* select(adaptSelectors.selectOutputMetadataById, metadataId);
        if (!metadata) { throw new Error(`Could not find customization metadata with id ${metadataId}`) }
        const duplicatedMetadata = duplicateModelCustomizationMetadataItem(metadata, newCustomizationOutputId);

        // handle filename metadata
        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE && newFilename !== undefined) {
            duplicatedMetadata.value = ensureFilenameHasDcmExtension(newFilename);
        }

        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE) {
            duplicateFilename = duplicatedMetadata.value;
        }

        newMetadata.push(duplicatedMetadata);
        duplicatedIds.push({ sourceId: metadataId, targetId: duplicatedMetadata.id });
    }

    // duplicate roi rules
    for (const roiRuleId of sourceCustomizationOutput.roiSelection.roiRules) {
        const roiRule = yield* select(adaptSelectors.selectRoiRuleById, roiRuleId);
        if (!roiRule) { throw new Error(`Could not find roi with id ${roiRuleId}`); }
        const duplicatedRoiRule = duplicateRoiRule(roiRule, newCustomizationOutputId);
        newRoiRules.push(duplicatedRoiRule);
        duplicatedIds.push({ sourceId: roiRuleId, targetId: duplicatedRoiRule.id });
    }

    // create new output
    const roiRuleIds = newRoiRules.map(r => r.id);
    const metadataIds = newMetadata.map(m => m.id);
    const duplicatedOutput = createNewAdaptCustomizationOutput(
        metadataIds,
        createNewAdaptRoiSelection(
            sourceCustomizationOutput.roiSelection.isEnabled,
            sourceCustomizationOutput.roiSelection.inclusion,
            roiRuleIds,
        ),
        cloneDeep(sourceCustomizationOutput.expectedResult),
        cloneDeep(sourceCustomizationOutput.pixelSpacing),
        cloneDeep(sourceCustomizationOutput.smoothingSigma),
        sourceCustomizationOutput.isValidMaskIncluded,
        newCustomizationBaseId || sourceCustomizationOutput.modelCustomizationBaseId,
        duplicateFilename,
        newCustomizationOutputId,
        true
    );

    const result: DuplicatedOutputEntities = {
        duplicatedOutput,
        newRoiRules,
        newMetadata,
        duplicatedIds,
    };

    return result;
};




/// watches for adapt config sagas

export function* watchSaveAdaptCustomizationSaga() {
    // take all adapt customization save requests as they come -- do limiting simultaenous saves by temporarily
    // disabling relevant ui elements instead of doing it with sagas here
    yield* takeEvery(modelCustomizationSaved, saveAdaptCustomizationSaga);
}

export function* watchAdaptCustomizationDuplicatedSaga() {
    yield* takeEvery(customizationBaseDuplicated, duplicateAdaptCustomizationSaga);
}

function* watchResetSingleAdaptCustomizationSaga() {
    yield* takeEvery(singleCustomizationReset, resetSingleAdaptCustomizationSaga);
}

function* watchResetAllAdaptCustomizationsSaga() {
    yield* takeEvery(allModelCustomizationsReset, resetAllAdaptCustomizationsSaga);
}

function* exportAdaptCustomizationPdf(appClient: MVisionAppClient) {
    try {
        yield* callApi({
            doApiCall: function* (client) {
                const blob: Blob = yield* call(async () => client.exportCustomizationPdfAsync(appClient, ModelType.Adapt));
                return blob;
            },
            onFinish: function* (result: Blob | undefined, error) {
                if (error || !result) {
                    const errorMessage = getApiErrorMessage(error, 'Adapt+ PDF export failed.');
                    yield* put(modelCustomizationExportFailed(errorMessage));
                    yield* put(pdfExportFinished());
                    return false;
                }

                const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
                const isLocalMode = appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local);
                const filename = `${EXPORT_ADAPT_JSON_FILE_PREFIX}${isLocalMode ? 'local' : (appClient.userName || 'customizations')}.pdf`;

                downloadBlobToDisk(result, filename);

                yield* put(modelCustomizationExportFailed(null));
                yield* put(pdfExportFinished());
                return true;
            },
        });
    } catch (error) {
        console.error('An error occurred in exportAdaptCustomizationPdf:', error);
        const errorMessage = getApiErrorMessage(error, 'Adapt+ PDF export failed.');
        yield* put(modelCustomizationExportFailed(errorMessage));
        yield* put(pdfExportFinished());
    }
}

export function* watchExportAdaptCustomizationPdfSaga() {
    while (true) {
        const action = yield* take(modelCustomizationPdfExportRequested);
        yield* put(pdfExportStarted());
        if (!action.payload) {
            yield* put(modelCustomizationExportFailed('PDF export of adapt customization has failed.'));
            yield* put(pdfExportFinished());
            continue;
        }
        yield* fork(exportAdaptCustomizationPdf, action.payload);
    }
}

function* watchExportAdaptCustomizationSaga() {
    while (true) {
        const action = yield* take(modelCustomizationExported);
        const errorMessage = 'Export of Adapt+ customization has failed.';
        if (!action.payload) {
            yield* put(modelCustomizationExportFailed(errorMessage));
            continue;
        }
        yield* fork(exportAdaptCustomization, action.payload);
    }
}

export function* watchImportAdaptCustomizationSaga() {
    while (true) {
        const action = yield* take(modelCustomizationImported);
        const errorMessage = 'No JSON data provided for Adapt+ import.';

        if (!action.payload) {
            yield* put(modelCustomizationImportFailed(errorMessage));
            continue;
        }

        yield* fork(importAdaptCustomization, action.payload);
    }
}


/** Returns all relevant watches to be added to a main root watch saga */
export function getWatchesForAdaptConfigSagas() {
    return [
        watchSaveAdaptCustomizationSaga(),
        watchResetSingleAdaptCustomizationSaga(),
        watchAdaptCustomizationDuplicatedSaga(),
        watchResetAllAdaptCustomizationsSaga(),
        watchExportAdaptCustomizationSaga(),
        watchExportAdaptCustomizationPdfSaga(),
        watchImportAdaptCustomizationSaga(),
    ];
}
